import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  TextInput,
  Textarea,
  Select,
  Button,
  Paper,
  Title,
  Text,
  Group,
  Box,
  Alert,
  Grid,
  Badge,
  ThemeIcon,
  Divider,
  LoadingOverlay,
  Stack,
  Center,
} from "@mantine/core";
import {
  IconBellRinging,
  IconSend,
  IconInfoCircle,
  IconCheck,
  IconAlertCircle,
  IconClick,
  IconWifi,
  IconBattery4,
  IconSignal4g,
} from "@tabler/icons-react";
import { API_BASE_URL } from "../../config/baseUrls";

export default function NotificationManagement() {
  const [title, setTitle] = useState("Hello Users");
  const [body, setBody] = useState("Wassup ????");
  const [screen, setScreen] = useState("");
  const [extraData, setExtraData] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const navigate = useNavigate();

  // --- LOGIC STARTS HERE (UNTOUCHED) ---
  const handleSend = async () => {
    setError("");
    setSuccess("");

    if (!title.trim()) {
      setError("Notification title is required");
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem("adminToken");

      if (!token) {
        setError("You are not authenticated. Please login again.");
        navigate("/admin");
        return;
      }

      const notificationData = {
        title: title.trim(),
        body: body.trim() || title.trim(),
        data: {},
      };

      if (screen) {
        notificationData.data.screen = screen;
      }

      if (extraData.trim()) {
        try {
          const parsedData = JSON.parse(extraData);
          notificationData.data = { ...notificationData.data, ...parsedData };
        } catch {
          notificationData.data.extraData = extraData.trim();
        }
      }

      if (Object.keys(notificationData.data).length === 0) {
        delete notificationData.data;
      }

      const response = await fetch(
        `${API_BASE_URL}/notification/admin/dashboard/custom/send`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(notificationData),
        },
      );

      const result = await response.json();

      if (result.success) {
        setSuccess(
          `✅ Notification sent successfully to ${result.result.successCount} devices! ` +
            `(${result.result.failCount} failed, ${result.result.invalidTokens} invalid tokens)`,
        );
      } else {
        setError(`Failed to send notification: ${result.error}`);
      }
    } catch (err) {
      console.error("Send notification error:", err);

      if (err.message.includes("401") || err.message.includes("403")) {
        setError("Authentication failed. Please login again.");
        localStorage.removeItem("adminToken");
        localStorage.removeItem("adminUser");
        localStorage.removeItem("IS_ADMIN");
        navigate("/admin");
      } else {
        setError("Network error. Please check your connection and try again.");
      }
    } finally {
      setLoading(false);
    }
  };
  // --- LOGIC ENDS HERE ---

  const screenOptions = [
    "Home",
    "Shop",
    "Project",
    "BadgeRankDetails",

    "BlogScreen",
    "QuizPlay",
    "QuizScore",
    "QuizPage",
    "QuizResponseCheck",
    "QuizRanking",
    "ReelSingle",
    "SearchOtaFeed",
    "FeedSingle",
    "QuizSingle",
    "QuizResponse",
  ];

  return (
    <Box>
      <Group justify="space-between" mb="lg">
        <Box>
          <Title order={2} className="ota-gradient-text">
            Push Notification Console
          </Title>
          <Text size="sm" c="dimmed">
            Broadcast messages to all active mobile users
          </Text>
        </Box>
        <Badge
          variant="dot"
          color="green"
          size="lg"
          radius="sm"
          className="shadow-sm"
        >
          SYSTEM ONLINE
        </Badge>
      </Group>

      <Grid gutter={40}>
        {/* LEFT COLUMN: Input Form */}
        <Grid.Col span={{ base: 12, lg: 7 }}>
          <Paper
            withBorder
            radius="md"
            p={30}
            className="shadow-sm h-full relative"
          >
            <LoadingOverlay
              visible={loading}
              overlayProps={{ radius: "sm", blur: 2 }}
            />

            {success && (
              <Alert
                icon={<IconCheck size={16} />}
                title="Success"
                color="green"
                mb="md"
                withCloseButton
                onClose={() => setSuccess("")}
              >
                {success}
              </Alert>
            )}
            {error && (
              <Alert
                icon={<IconAlertCircle size={16} />}
                title="Error"
                color="red"
                mb="md"
                withCloseButton
                onClose={() => setError("")}
              >
                {error}
              </Alert>
            )}

            <Stack gap="lg">
              <TextInput
                label={
                  <Text fw={600} size="sm">
                    Notification Title{" "}
                    <Text span c="red">
                      *
                    </Text>
                  </Text>
                }
                placeholder="Enter title here"
                description="The bold header text of your notification"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                radius="md"
                size="md"
              />

              <Textarea
                label={
                  <Text fw={600} size="sm">
                    Notification Body
                  </Text>
                }
                placeholder="Enter message here"
                description="The main content text. If empty, title will be used."
                value={body}
                onChange={(e) => setBody(e.target.value)}
                minRows={3}
                radius="md"
                size="md"
              />

              <Divider
                label="Navigation Action"
                labelPosition="center"
                my="xs"
                color="gray.2"
              />

              <Select
                label={
                  <Text fw={600} size="sm">
                    Target Screen (Optional)
                  </Text>
                }
                placeholder="Select a screen to open on tap"
                data={screenOptions}
                searchable
                value={screen}
                onChange={setScreen}
                radius="md"
                size="md"
                leftSection={<IconClick size={16} />}
              />

              <Textarea
                label={
                  <Text fw={600} size="sm">
                    Extra Data Payload (JSON)
                  </Text>
                }
                placeholder='{"quizId": "123"}'
                description="Advanced: Pass custom JSON data to the app"
                value={extraData}
                onChange={(e) => setExtraData(e.target.value)}
                minRows={2}
                radius="md"
                size="md"
                styles={{
                  input: { fontFamily: "monospace", fontSize: "13px" },
                }}
              />

              <Group justify="flex-end" mt="md">
                <Button
                  size="md"
                  w={220} // Optimized width
                  onClick={handleSend}
                  disabled={!title.trim()}
                  loading={loading}
                  className="btn-ota-primary shadow-md hover:shadow-lg transition-all"
                  leftSection={<IconSend size={18} />}
                  radius="md"
                >
                  Send Broadcast
                </Button>
              </Group>
            </Stack>
          </Paper>
        </Grid.Col>

        {/* RIGHT COLUMN: iPhone Preview */}
        <Grid.Col span={{ base: 12, lg: 5 }}>
          <Box className="sticky top-6">
            <Text fw={700} c="dimmed" mb="md" tt="uppercase" size="sm" lts={1}>
              Live Preview
            </Text>

            <Center>
              {/* THE PHONE CONTAINER */}
              <Box
                w={300}
                h={600}
                bg="black"
                style={{
                  borderRadius: "44px",
                  border: "10px solid #121212",
                  position: "relative",
                  overflow: "hidden",
                  boxShadow: "0 20px 40px -10px rgba(0,0,0,0.3)",
                }}
              >
                {/* Wallpaper Gradient */}
                <Box className="absolute inset-0 bg-gradient-to-b from-sky-200 via-cyan-100 to-blue-200" />

                {/* Status Bar */}
                <Group
                  justify="space-between"
                  px={24}
                  pt={14}
                  className="absolute top-0 w-full text-slate-800 z-20"
                >
                  <Text size="xs" fw={600}>
                    09:41
                  </Text>
                  <Group gap={4}>
                    <IconSignal4g size={12} stroke={2.5} />
                    <IconWifi size={12} stroke={2.5} />
                    <IconBattery4 size={14} stroke={2.5} />
                  </Group>
                </Group>

                {/* Dynamic Island Notch */}
                <Box
                  className="absolute top-2 left-1/2 transform -translate-x-1/2 bg-black z-30"
                  w={90}
                  h={26}
                  style={{ borderRadius: "20px" }}
                />

                {/* Lock Screen Clock */}
                <Stack
                  align="center"
                  gap={0}
                  mt={70}
                  className="relative z-10 text-white drop-shadow-sm"
                >
                  <Text
                    fz={64}
                    fw={300}
                    lh={1}
                    style={{ fontFamily: "system-ui" }}
                  >
                    09:41
                  </Text>
                  <Text size="md" fw={500}>
                    Wednesday, January 28
                  </Text>
                </Stack>

                {/* Notification Card */}
                <Box px={12} mt={20} className="relative z-10">
                  <Paper
                    radius="xl"
                    p="sm"
                    className="bg-white/60 backdrop-blur-xl shadow-sm"
                    style={{ border: "1px solid rgba(255,255,255,0.4)" }}
                  >
                    {/* Card Header */}
                    <Group justify="space-between" mb={6} align="center" px={4}>
                      <Group gap={6}>
                        <ThemeIcon
                          size={20}
                          radius="md"
                          color="blue"
                          variant="filled"
                        >
                          <IconBellRinging size={12} />
                        </ThemeIcon>
                        <Text
                          size="10px"
                          fw={700}
                          tt="uppercase"
                          c="dark"
                          opacity={0.6}
                        >
                          OTA APP
                        </Text>
                      </Group>
                      <Text size="10px" c="dimmed">
                        now
                      </Text>
                    </Group>

                    {/* Content */}
                    <Box px={4} pb={4}>
                      <Text size="sm" fw={700} c="black" lh={1.2} mb={2}>
                        {title || "Hello Users"}
                      </Text>
                      <Text
                        size="xs"
                        c="black"
                        opacity={0.8}
                        lh={1.3}
                        lineClamp={3}
                      >
                        {body || title || "Wassup ????"}
                      </Text>
                    </Box>
                  </Paper>
                </Box>

                {/* Bottom Handle */}
                <Box
                  className="absolute bottom-2 left-1/2 transform -translate-x-1/2 bg-black/20"
                  w={110}
                  h={4}
                  style={{ borderRadius: "4px" }}
                />
              </Box>
            </Center>

            {/* Pro Tip Box */}
            <Paper
              withBorder
              p="md"
              radius="md"
              mt="xl"
              bg="blue.0"
              className="border-blue-100"
            >
              <Group>
                <ThemeIcon variant="light" color="blue" radius="xl">
                  <IconInfoCircle size={16} />
                </ThemeIcon>
                <Box>
                  <Text size="xs" fw={700} c="blue.8">
                    Pro Tip
                  </Text>
                  <Text size="xs" c="blue.7">
                    Notifications with <b>Short Titles</b> (under 40 chars) have
                    a 20% higher open rate.
                  </Text>
                </Box>
              </Group>
            </Paper>
          </Box>
        </Grid.Col>
      </Grid>
    </Box>
  );
}
